import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BubbleMenu } from '@tiptap/react/menus'
import { FaBold, FaItalic, FaUnderline } from 'react-icons/fa6'
import { Check, ChevronDown, CircleDot, Code, Link2, MessageSquarePlus, Unlink } from 'lucide-react'
import { createTaskFromSelection, getEditorActions } from './editorActions'
import type { Editor } from '@tiptap/core'
import type { IconType } from 'react-icons'
import type { ComponentType } from 'react'
import { cn } from '@/lib/utils'

export interface FloatingFormatToolbarItem {
  format: 'bold' | 'italic' | 'underline' | 'strike' | 'strikethrough'
  Icon?: IconType | ComponentType<{ className?: string }>
  label: string
}

export const DEFAULT_TOOLBAR_ITEMS: Array<FloatingFormatToolbarItem> = [
  { format: 'bold', Icon: FaBold, label: 'Bold' },
  { format: 'italic', Icon: FaItalic, label: 'Italic' },
  { format: 'underline', Icon: FaUnderline, label: 'Underline' },
]

interface FloatingToolbarProps {
  editor: Editor
  items?: Array<FloatingFormatToolbarItem>
  /** Turn into, inline code, link and Create task (design 7b). Docs only. */
  enableBlockControls?: boolean
  enableComments?: boolean
  onAddComment?: () => void
}

const FORMAT_COMMANDS: Record<string, (editor: Editor) => boolean> = {
  bold: (editor: Editor) => editor.chain().focus().toggleBold().run(),
  italic: (editor: Editor) => editor.chain().focus().toggleItalic().run(),
  underline: (editor: Editor) => editor.chain().focus().toggleUnderline().run(),
  strike: (editor: Editor) => editor.chain().focus().toggleStrike().run(),
  strikethrough: (editor: Editor) => editor.chain().focus().toggleStrike().run(),
}

const FORMAT_ACTIVE_CHECK: Record<string, (editor: Editor) => boolean> = {
  bold: (editor: Editor) => editor.isActive('bold'),
  italic: (editor: Editor) => editor.isActive('italic'),
  underline: (editor: Editor) => editor.isActive('underline'),
  strike: (editor: Editor) => editor.isActive('strike'),
  strikethrough: (editor: Editor) => editor.isActive('strike'),
}

type BlockOption = {
  id: string
  labelKey: string
  labelArgs?: Record<string, unknown>
  isActive: (editor: Editor) => boolean
  apply: (editor: Editor) => void
}

/** Block types a selection can be turned into. */
const BLOCK_OPTIONS: Array<BlockOption> = [
  {
    id: 'text',
    labelKey: 'editor.toolbar.text',
    isActive: (e) => e.isActive('paragraph') && !e.isActive('bulletList') && !e.isActive('orderedList'),
    apply: (e) => e.chain().focus().setParagraph().run(),
  },
  ...([1, 2, 3] as const).map((level) => ({
    id: `h${level}`,
    labelKey: 'editor.toolbar.heading',
    labelArgs: { level },
    isActive: (e: Editor) => e.isActive('heading', { level }),
    apply: (e: Editor) => e.chain().focus().setNode('heading', { level }).run(),
  })),
  {
    id: 'bullet',
    labelKey: 'editor.toolbar.bulleted',
    isActive: (e) => e.isActive('bulletList'),
    apply: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    id: 'ordered',
    labelKey: 'editor.toolbar.numbered',
    isActive: (e) => e.isActive('orderedList'),
    apply: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    id: 'todo',
    labelKey: 'editor.toolbar.todo',
    isActive: (e) => e.isActive('taskList'),
    apply: (e) => e.chain().focus().toggleTaskList().run(),
  },
  {
    id: 'toggle',
    labelKey: 'editor.toolbar.toggle',
    isActive: (e) => e.isActive('details'),
    apply: (e) => e.chain().focus().setDetails().run(),
  },
  {
    id: 'callout',
    labelKey: 'editor.toolbar.callout',
    isActive: (e) => e.isActive('callout'),
    apply: (e) => e.chain().focus().toggleCallout().run(),
  },
  {
    id: 'quote',
    labelKey: 'editor.toolbar.quote',
    isActive: (e) => e.isActive('blockquote'),
    apply: (e) => e.chain().focus().toggleBlockquote().run(),
  },
  {
    id: 'code',
    labelKey: 'editor.toolbar.code',
    isActive: (e) => e.isActive('codeBlock'),
    apply: (e) => e.chain().focus().toggleCodeBlock().run(),
  },
]

const buttonClass = (active = false) =>
  cn(
    'flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
    active ? 'bg-white/15 text-white' : 'text-white/75 hover:bg-white/10 hover:text-white',
  )

function normaliseUrl(raw: string): string {
  const value = raw.trim()
  if (!value) return ''
  return /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith('/') ? value : `https://${value}`
}

/**
 * Selection toolbar (design 7b): an ink bar with Turn into, marks, link,
 * comment and — on docs — Create task.
 */
export function FloatingToolbar({
  editor,
  items,
  enableBlockControls = false,
  enableComments,
  onAddComment,
}: FloatingToolbarProps) {
  const { t } = useTranslation('common')
  const toolbarItems = items && items.length > 0 ? items : DEFAULT_TOOLBAR_ITEMS
  const [mode, setMode] = useState<'format' | 'blocks' | 'link'>('format')
  const [linkValue, setLinkValue] = useState('')
  const linkInputRef = useRef<HTMLInputElement>(null)

  // Each new selection starts on the main bar.
  useEffect(() => {
    const reset = () => setMode('format')
    editor.on('selectionUpdate', reset)
    return () => {
      editor.off('selectionUpdate', reset)
    }
  }, [editor])

  useEffect(() => {
    if (mode === 'link') linkInputRef.current?.focus()
  }, [mode])

  const handleToggleFormat = useCallback(
    (format: FloatingFormatToolbarItem['format']) => {
      FORMAT_COMMANDS[format]?.(editor)
    },
    [editor],
  )

  const canCreateTask =
    enableBlockControls &&
    !!getEditorActions(editor).createTask &&
    editor.extensionManager.extensions.some((e) => e.name === 'entityLink')
  const hasLink = editor.extensionManager.extensions.some((e) => e.name === 'link')
  const activeBlock: BlockOption | undefined =
    BLOCK_OPTIONS.find((option) => option.isActive(editor)) ?? BLOCK_OPTIONS[0]

  const applyLink = () => {
    const href = normaliseUrl(linkValue)
    const chain = editor.chain().focus().extendMarkRange('link')
    if (href) chain.setLink({ href }).run()
    else chain.unsetLink().run()
    setMode('format')
  }

  const keepSelection = (event: React.MouseEvent) => event.preventDefault()

  return (
    <BubbleMenu
      editor={editor}
      className="z-50 flex items-center gap-0.5 rounded-lg bg-[#1f1d1a] p-1 text-sm text-white shadow-xl dark:bg-[#0f0e0d] dark:ring-1 dark:ring-white/10"
    >
      {mode === 'link' ? (
        <form
          className="flex items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault()
            applyLink()
          }}
        >
          <input
            ref={linkInputRef}
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                setMode('format')
                editor.commands.focus()
              }
            }}
            placeholder={t('editor.toolbar.linkPlaceholder')}
            className="h-7 w-56 rounded-md bg-white/10 px-2 text-base text-white outline-none placeholder:text-white/50 md:text-[13px]"
          />
          <button type="submit" aria-label={t('editor.toolbar.link')} className={buttonClass()}>
            <Check className="h-4 w-4" />
          </button>
          {editor.isActive('link') && (
            <button
              type="button"
              aria-label={t('editor.toolbar.removeLink')}
              title={t('editor.toolbar.removeLink')}
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetLink().run()
                setMode('format')
              }}
              className={buttonClass()}
            >
              <Unlink className="h-4 w-4" />
            </button>
          )}
        </form>
      ) : mode === 'blocks' ? (
        <div role="menu" className="flex flex-col py-0.5">
          {BLOCK_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="menuitem"
              onMouseDown={keepSelection}
              onClick={() => {
                option.apply(editor)
                setMode('format')
              }}
              className={cn(buttonClass(option.isActive(editor)), 'justify-between gap-6 px-2')}
            >
              {t(option.labelKey, option.labelArgs)}
              {option.isActive(editor) && <Check className="h-3.5 w-3.5" />}
            </button>
          ))}
        </div>
      ) : (
        <>
          {enableBlockControls && (
            <>
              <button
                type="button"
                aria-haspopup="menu"
                title={t('editor.toolbar.turnInto')}
                onMouseDown={keepSelection}
                onClick={() => setMode('blocks')}
                className={cn(buttonClass(), 'gap-1 px-2')}
              >
                {activeBlock ? t(activeBlock.labelKey, activeBlock.labelArgs) : t('editor.toolbar.turnInto')}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <div className="mx-0.5 h-4 w-px bg-white/20" />
            </>
          )}
          {toolbarItems.map(({ format, Icon, label }) => {
            const isActive = FORMAT_ACTIVE_CHECK[format]?.(editor) ?? false
            return (
              <button
                key={format}
                type="button"
                aria-label={label}
                aria-pressed={isActive}
                title={label}
                onMouseDown={keepSelection}
                onClick={() => handleToggleFormat(format)}
                className={buttonClass(isActive)}
              >
                {Icon ? <Icon className="h-3.5 w-3.5" /> : <span>{label}</span>}
              </button>
            )
          })}
          {enableBlockControls && (
            <button
              type="button"
              aria-label={t('editor.toolbar.inlineCode')}
              aria-pressed={editor.isActive('code')}
              title={t('editor.toolbar.inlineCode')}
              onMouseDown={keepSelection}
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={buttonClass(editor.isActive('code'))}
            >
              <Code className="h-3.5 w-3.5" />
            </button>
          )}
          {enableBlockControls && hasLink && (
            <button
              type="button"
              aria-label={t('editor.toolbar.link')}
              aria-pressed={editor.isActive('link')}
              title={t('editor.toolbar.link')}
              onMouseDown={keepSelection}
              onClick={() => {
                setLinkValue((editor.getAttributes('link').href as string | undefined) ?? '')
                setMode('link')
              }}
              className={buttonClass(editor.isActive('link'))}
            >
              <Link2 className="h-3.5 w-3.5" />
            </button>
          )}
          {enableComments && onAddComment ? (
            <>
              <div className="mx-0.5 h-4 w-px bg-white/20" />
              <button
                type="button"
                aria-label={t('editor.toolbar.comment')}
                title={t('editor.toolbar.comment')}
                onMouseDown={keepSelection}
                onClick={onAddComment}
                className={buttonClass()}
              >
                <MessageSquarePlus className="h-3.5 w-3.5" />
              </button>
            </>
          ) : null}
          {canCreateTask && (
            <button
              type="button"
              onMouseDown={keepSelection}
              onClick={() => void createTaskFromSelection(editor)}
              className="ml-1 flex h-7 items-center gap-1.5 rounded-md bg-brand px-2.5 text-[13px] font-semibold text-brand-foreground transition-opacity hover:opacity-90"
            >
              <CircleDot className="h-3.5 w-3.5" />
              {t('editor.toolbar.createTask')}
            </button>
          )}
        </>
      )}
    </BubbleMenu>
  )
}
